// Single source of truth for device online / staleness / protocol checks.
//
// These rules were previously copy-pasted inline across HomeCentralSection,
// DeviceList, DashboardInsights, DeviceDetailsPanel and DeviceCommandCenter. When
// one copy changed and the others didn't, the dashboard list and the detail panel
// disagreed about whether a device was online (a recurring regression). Keeping the
// rules here — and unit-tested in deviceStatus.test.js — means every surface reads
// the same definition.

// A GT06/FT920 tracker frame older than this is "stale": the socket may still be
// up, but the position is no longer trustworthy, so the list shows it OFFLINE.
// Mirrors the backend GT06_STALE_THRESHOLD_MS.
//
// 6 min (was 5 min) — a device posting on its normal 5-min cadence must never
// read OFFLINE between two on-time pings; the old 5-min window equaled the ping
// interval itself, so any small delay (network jitter, a slightly late fix)
// flipped a perfectly healthy tracker OFFLINE and back ONLINE on the next
// packet. 6 min gives a full minute of slack past the 5-min cadence before a
// tracker that's actually gone silent is shown offline. Mirrors Android's
// util/OnlineRule.TRACKER_STALE_MS.
export const GT06_STALE_THRESHOLD_MS = 6 * 60 * 1000;

// A MobiTrack user who hasn't posted within this window is considered OFFLINE.
export const ONLINE_THRESHOLD_MS = 3 * 60 * 1000;

/**
 * True when a tracker's most recent frame is older than the stale threshold.
 * A missing/blank timestamp is treated as "not stale" (falsy) — the caller's
 * socket check decides online/offline in that case, matching prior behaviour.
 * `now` is injectable for deterministic tests.
 */
export const isGt06Stale = (lastFrameUtc, now = Date.now()) => {
  if (!lastFrameUtc) return false;
  return now - new Date(lastFrameUtc).getTime() > GT06_STALE_THRESHOLD_MS;
};

/**
 * True when a tracker's last REAL GPS fix (not just any contact — a heartbeat has no fix
 * at all) is older than the stale threshold, or there was never one. Deliberately distinct
 * from `isGt06Stale`, which is fed `lastFrameUtc` — a timestamp that heartbeats *also*
 * advance (they carry their own `dateTime`, just no lat/lng/speed) — so a device can stay
 * "online" purely on heartbeats indefinitely while its last known speed silently ages: it
 * can show e.g. "37 km/h" continuously for an hour just because it hasn't sent a real fix
 * since, not because it's actually moving. Use this to gate *speed* specifically, not
 * online/offline or ignition — ignition already reflects the real, live heartbeat `accOn`
 * (kept current by the WS handler even between fixes), so it shouldn't be forced off just
 * because the position itself has gone stale. Deliberately the same window as
 * `GT06_STALE_THRESHOLD_MS` — one story, not two: a tracker stops looking "confidently in
 * motion" at the same point it would otherwise start looking "maybe offline". Mirrors the
 * backend's `VTrackAppConstant.GT06_POSITION_STALE_MS` / `AssetService
 * .enrichWithPositionStaleness` and Android's `OnlineRule.isPositionStale`.
 */
export const isGt06PositionStale = (lastFixUtc, now = Date.now()) => {
  if (!lastFixUtc) return true;
  return now - new Date(lastFixUtc).getTime() >= GT06_STALE_THRESHOLD_MS;
};

/**
 * Whether a GT06 device's currently-displayed speed should be treated as stale, given a
 * device's `assetType` and its parsed `additionalInfo`-shaped object.
 *
 * Only ever applies to GT06_TRACKER devices — every other asset type (GPS_LOCK, MOBI_TRACK,
 * BT_LOCK) returns false unconditionally, since this rule doesn't apply to them.
 *
 * Reads `info.fixDateTime` in preference to `info.dateTime`: on a real GPS fix both carry the
 * same timestamp, but a GT06 heartbeat/presence frame ALSO carries its own `dateTime` (just a
 * contact time, no position) — code that keeps `dateTime` refreshed on every frame (as
 * Tracking.js's WS handler does, for other, unrelated uses of `dateTime`) would otherwise look
 * permanently "fresh" and never trip this gate. `fixDateTime` must only ever be written when a
 * frame is confirmed to carry a real, trusted fix (see `hasReliableGpsFix`) — never on a bare
 * heartbeat. Falls back to `info.dateTime` for a device whose `additionalInfo` was seeded
 * straight from a REST snapshot and has no `fixDateTime` yet: at that point `dateTime` IS the
 * last real fix time, because the backend only ever advances `additionalInfo.dateTime` from a
 * real fix in the first place (see backend's `AssetService.enrichWithPositionStaleness`) — it's
 * only a *live* WS heartbeat frame that overloads `dateTime` with a mere contact time.
 *
 * Extracted so this exact rule — used by Tracking.js's quick-filter counts (`isMoving`/`isIdle`)
 * and its map marker's displayed speed — is unit-testable without rendering the component.
 */
export const gt06SpeedIsStale = (assetType, info, now = Date.now()) => {
  if (assetType !== "GT06_TRACKER") return false;
  return isGt06PositionStale(info?.fixDateTime ?? info?.dateTime ?? null, now);
};

/**
 * True when a MobiTrack user posted within the online window.
 * A missing timestamp is OFFLINE.
 */
export const isMobiTrackOnline = (utcTimestamp, now = Date.now()) => {
  if (!utcTimestamp) return false;
  return now - new Date(utcTimestamp).getTime() <= ONLINE_THRESHOLD_MS;
};

/**
 * True when a tracker speaks the FT920 (JT808) wire protocol. Accepts either a
 * device object ({ protocol }) or a protocol string. Case-insensitive so
 * "FT920"/"ft920" both match; null/undefined/other → false (i.e. GT06).
 */
export const isFt920 = (deviceOrProtocol) => {
  const protocol =
    typeof deviceOrProtocol === "string"
      ? deviceOrProtocol
      : deviceOrProtocol?.protocol;
  return (protocol || "").toUpperCase() === "FT920";
};

/**
 * Whether the unread-alerts bell should render on a device row.
 *
 * The alert *count* endpoint (`/getAlertsCount`) counts every row in the shared
 * `location_alert_report` table, which includes Track Point (MOBI_TRACK) duty alerts
 * (`DUTY_OVERRUN_WARNING` at 12h, `DUTY_AUTO_ENDED` at the 14h cap). That lights the bell.
 * But the alert-*detail* pipeline — the backend `/getAlerts` parser and `AlertPopupDialog` —
 * only understands GL600 lock binary frames: a duty alert's plain-text `location_alert`
 * can't be hex/GL600-parsed, so the click returns nothing and the popup shows
 * "No alerts to display". The bell is a dead affordance for Track Point.
 *
 * Until the detail path learns to render duty alerts, suppress the bell on MOBI_TRACK rows
 * so it isn't a misleading no-op. Other device types are unaffected.
 */
export const showAlertBell = (device) =>
  !!device && device.alertStatus !== "-" && device.assetType !== "MOBI_TRACK";

/**
 * True when a GT06/FT920 telemetry frame's latitude/longitude should be trusted as
 * a real GPS fix, rather than just applied because the fields happen to be present.
 *
 * A GT06/FT920 frame always carries latitude/longitude — even a no-fix frame sends
 * 0.0/0.0 — so field presence alone was never a reliable signal: it only "worked" by
 * the accident of 0 being falsy elsewhere downstream, and would misclassify a device
 * with a genuine fix sitting exactly on the equator/prime meridian as having none.
 * The backend's `gpsFix` flag (mirrors GT06LocationReport.isGpsFix() end-to-end) is
 * the authoritative signal — trust that instead.
 */
export const hasReliableGpsFix = (hasLatitude, hasLongitude, gpsFixValue) =>
  hasLatitude && hasLongitude && gpsFixValue === true;

/**
 * Picks the most recent of two ISO-ish timestamps, ignoring anything null/unparseable. Used to
 * guard the dashboard's "last ping" column against ever regressing: navigating away from the
 * dashboard and back re-fetches the asset list and re-seeds `dateTimeF1`/`lastFrameUtc` from
 * scratch, and if that re-fetch is served an older `lastContactUtc` than the value the socket had
 * already pushed live this session (a slow/duplicate in-flight request resolving out of order, a
 * read against a lagging replica, etc.), the row would visibly jump backwards in time and flip
 * Offline even though nothing about the device actually changed. Pairs with
 * `utils/lastContactCache.js`, which remembers the freshest value seen per asset across a
 * component remount (plain React state doesn't survive that).
 */
export const pickFreshestIso = (a, b) => {
  const msA = a ? new Date(a).getTime() : NaN;
  const msB = b ? new Date(b).getTime() : NaN;
  const validA = Number.isFinite(msA);
  const validB = Number.isFinite(msB);
  if (!validA && !validB) return null;
  if (!validA) return b;
  if (!validB) return a;
  return msA >= msB ? a : b;
};
